import { ErroApi } from '@entregas/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// "Sem conexão" do painel (decisão 68 e item 6 do quadro): a lista continua na tela com os dados
// do cache; só a linha de estado avisa e os botões que gravam ficam mudos. Sem conexão é o
// navegador dizer offline OU uma leitura do banco falhar por rede (Wi-Fi ligado sem internet).

export interface EstadoDaConexao {
  online: boolean;
  /** Quando caiu (para "Sem conexão desde 19:21"); null com conexão. */
  desde: Date | null;
  /** Relê agora as consultas da tela. */
  tentarAgora: () => void;
}

const SEMPRE_ONLINE: EstadoDaConexao = { online: true, desde: null, tentarAgora: () => {} };

const Contexto = createContext<EstadoDaConexao | null>(null);

function navegadorOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

function ehSemConexao(erro: unknown): boolean {
  return erro instanceof ErroApi && erro.codigo === 'sem_conexao';
}

export function ProvedorDaConexao({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [estado, setEstado] = useState<{ online: boolean; desde: Date | null }>(() =>
    navegadorOnline() ? { online: true, desde: null } : { online: false, desde: new Date() },
  );

  useEffect(() => {
    // Cair de novo não troca o "desde"; voltar só vale com o navegador online.
    const cair = () => setEstado((e) => (e.online ? { online: false, desde: new Date() } : e));
    const voltar = () => {
      if (navegadorOnline()) setEstado((e) => (e.online ? e : { online: true, desde: null }));
    };

    window.addEventListener('offline', cair);
    window.addEventListener('online', voltar);
    const pararConsultas = qc.getQueryCache().subscribe((evento) => {
      if (evento.type !== 'updated') return;
      if (evento.action.type === 'error' && ehSemConexao(evento.action.error)) cair();
      if (evento.action.type === 'success') voltar();
    });
    const pararGravacoes = qc.getMutationCache().subscribe((evento) => {
      if (evento.type !== 'updated') return;
      if (evento.action.type === 'error' && ehSemConexao(evento.action.error)) cair();
      if (evento.action.type === 'success') voltar();
    });
    return () => {
      window.removeEventListener('offline', cair);
      window.removeEventListener('online', voltar);
      pararConsultas();
      pararGravacoes();
    };
  }, [qc]);

  const tentarAgora = useCallback(() => {
    void qc.refetchQueries({ type: 'active' });
  }, [qc]);

  const valor = useMemo(() => ({ ...estado, tentarAgora }), [estado, tentarAgora]);
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

/** Fora do provedor (testes de outras telas) vale "online": nada fica mudo sem motivo. */
export function useConexao(): EstadoDaConexao {
  return useContext(Contexto) ?? SEMPRE_ONLINE;
}
